import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Typography,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Tag,
  DatePicker,
  InputNumber,
  Grid,
  Dropdown,
  Drawer,
  Descriptions,
  Tooltip,
  Pagination
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  ReloadOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

interface Employee {
  id: number;
  employee_no: string;
  name: string;
  hire_date: string;
  department_position: string;
  basic_salary: number;
  position_salary: number;
  seniority_salary: number;
  gender: '男' | '女';
  birth_date?: string;
  id_card?: string;
  education?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  address?: string;
  remark?: string;
  attachments?: string;
  business_order: '是' | '否';
  created_at: string;
  updated_at: string;
}

const EmployeeManagement: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // 性别选项
  const genderOptions = [
    { label: '男', value: '男' },
    { label: '女', value: '女' },
  ];

  // 业务做单选项
  const businessOrderOptions = [
    { label: '是', value: '是' },
    { label: '否', value: '否' },
  ];

  // 学历选项
  const educationOptions = [
    { label: '小学', value: '小学' },
    { label: '初中', value: '初中' },
    { label: '高中', value: '高中' },
    { label: '中专', value: '中专' },
    { label: '大专', value: '大专' },
    { label: '本科', value: '本科' },
    { label: '硕士', value: '硕士' },
    { label: '博士', value: '博士' },
  ];

  // 获取员工列表
  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        current: currentPage.toString(),
        pageSize: pageSize.toString(),
      });

      if (searchText) {
        params.append('search_keyword', searchText);
      }


      const response = await fetch(`/api/employees?${params}`);
      const result = await response.json();

      if (result.code === 0) {
        setEmployees(result.data);
        setTotal(result.total);
      } else {
        messageApi.error(result.message || '获取员工列表失败');
      }
    } catch (error) {
      console.error('获取员工列表失败:', error);
      messageApi.error('获取员工列表失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchText]);

  // 加载数据
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // 清理搜索定时器
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // 查看员工详情
  const handleView = useCallback(async (employee: Employee) => {
    setSelectedEmployee(employee);
    setDrawerVisible(true);
  }, []);



  // 显示新增/编辑模态框
  const showModal = (employee?: Employee) => {
    if (employee) {
      setEditingEmployee(employee);
      form.setFieldsValue({
        ...employee,
        hire_date: employee.hire_date ? dayjs(employee.hire_date) : undefined,
        birth_date: employee.birth_date ? dayjs(employee.birth_date) : undefined,
      });
    } else {
      setEditingEmployee(null);
      form.resetFields();
      form.setFieldsValue({
        gender: '男',
        business_order: '否',
        basic_salary: '0',
        position_salary: '0',
        seniority_salary: '0',
      });
    }
    setModalVisible(true);
  };

  // 处理表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const employeeData = {
        ...values,
        hire_date: values.hire_date ? values.hire_date.format('YYYY-MM-DD') : undefined,
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : undefined,
        // 确保工资字段是数字类型
        basic_salary: Number(values.basic_salary) || 0,
        position_salary: Number(values.position_salary) || 0,
        seniority_salary: Number(values.seniority_salary) || 0,
      };

      const url = editingEmployee 
        ? `/api/employees/${editingEmployee.id}` 
        : '/api/employees';
      
      const method = editingEmployee ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(employeeData),
      });

      const result = await response.json();

      if (result.code === 0) {
        messageApi.success(editingEmployee ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchEmployees();
      } else {
        messageApi.error(result.message || '操作失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      messageApi.error('提交失败');
    }
  };

  // 删除员工
  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(`/api/employees/${id}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (result.code === 0) {
        messageApi.success('删除成功');
        fetchEmployees();
      } else {
        messageApi.error(result.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      messageApi.error('删除失败');
    }
  };

  // 处理搜索
  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    setCurrentPage(1);
    
    // 清除之前的定时器
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // 设置新的定时器，实现防抖搜索
    const timeout = setTimeout(() => {
      fetchEmployees();
    }, 300);
    
    setSearchTimeout(timeout);
  }, [searchTimeout]);

  // 导出CSV文件
  const exportToCSV = useCallback(async () => {
    setExportLoading(true);
    try {
      // 获取所有数据用于导出
      const response = await fetch('/api/employees?current=1&pageSize=10000');
      const result = await response.json();
      
      if (result.code !== 0) {
        throw new Error('获取导出数据失败');
      }
      
      // 准备导出数据
      const exportData = result.data.map((employee: Employee) => ({
        '员工编号': employee.employee_no,
        '姓名': employee.name,
        '入职日期': employee.hire_date,
        '部门岗位': employee.department_position,
        '性别': employee.gender,
        '基本工资': employee.basic_salary,
        '岗位工资': employee.position_salary,
        '工龄工资': employee.seniority_salary,
        '业务做单': employee.business_order,
        '出生日期': employee.birth_date || '',
        '身份证号': employee.id_card || '',
        '学历': employee.education || '',
        '电话': employee.phone || '',
        '手机': employee.mobile || '',
        '邮箱': employee.email || '',
        '住址': employee.address || '',
        '备注': employee.remark || ''
      }));

      // 创建CSV内容
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map((row: Record<string, any>) => 
          headers.map(header => `"${row[header] || ''}"`).join(',')
        )
      ].join('\n');

      // 创建下载链接
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `员工数据_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      messageApi.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      messageApi.error('导出失败');
    } finally {
      setExportLoading(false);
    }
  }, []);

  // 导出Excel文件
  const exportToExcel = useCallback(async () => {
    setExportLoading(true);
    try {
      // 获取所有数据用于导出
      const response = await fetch('/api/employees?current=1&pageSize=10000');
      const result = await response.json();
      
      if (result.code !== 0) {
        throw new Error('获取导出数据失败');
      }
      
      // 准备导出数据
      const exportData = result.data.map((employee: Employee) => ({
        '员工编号': employee.employee_no,
        '姓名': employee.name,
        '入职日期': employee.hire_date,
        '部门岗位': employee.department_position,
        '性别': employee.gender,
        '基本工资': employee.basic_salary,
        '岗位工资': employee.position_salary,
        '工龄工资': employee.seniority_salary,
        '业务做单': employee.business_order,
        '出生日期': employee.birth_date || '',
        '身份证号': employee.id_card || '',
        '学历': employee.education || '',
        '电话': employee.phone || '',
        '手机': employee.mobile || '',
        '邮箱': employee.email || '',
        '住址': employee.address || '',
        '备注': employee.remark || ''
      }));

      // 创建Excel内容（这里使用CSV格式，实际项目中可以使用xlsx库）
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map((row: Record<string, any>) => 
          headers.map(header => `"${row[header] || ''}"`).join(',')
        )
      ].join('\n');

      // 创建下载链接
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `员工数据_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      messageApi.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      messageApi.error('导出失败');
    } finally {
      setExportLoading(false);
    }
  }, []);

  // 渲染员工卡片（移动端）
  const renderEmployeeCard = useCallback((employee: Employee) => (
    <Card 
      key={employee.id} 
      size="small" 
      style={{ marginBottom: 12 }}
      actions={[
        <Tooltip title="查看详情" key="view">
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => handleView(employee)}
          >
            详情
          </Button>
        </Tooltip>,
        <Tooltip title="编辑" key="edit">
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => showModal(employee)}
          >
            编辑
          </Button>
        </Tooltip>,
        <Popconfirm
          title="确定要删除这个员工吗？"
          onConfirm={() => handleDelete(employee.id)}
          okText="确定"
          cancelText="取消"
          key="delete"
        >
          <Tooltip title="删除">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              删除
            </Button>
          </Tooltip>
        </Popconfirm>
      ]}
    >
      <Row gutter={[8, 6]}>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#8c8c8c' }}>员工编号：</span>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: 'bold', fontSize: 16 }}>{employee.employee_no}</span>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#8c8c8c' }}>姓名：</span>
            <span style={{ textAlign: 'right' }}>{employee.name}</span>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#8c8c8c' }}>部门岗位：</span>
            <span style={{ textAlign: 'right' }}>{employee.department_position}</span>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#8c8c8c' }}>性别：</span>
            <div style={{ textAlign: 'right' }}>
              <Tag color={employee.gender === '男' ? 'blue' : 'pink'}>{employee.gender}</Tag>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#8c8c8c' }}>入职日期：</span>
            <span style={{ textAlign: 'right' }}>{employee.hire_date ? dayjs(employee.hire_date).format('YYYY-MM-DD') : '-'}</span>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#8c8c8c' }}>基本工资：</span>
            <span style={{ textAlign: 'right' }}>¥{(Number(employee.basic_salary) || 0).toFixed(2)}</span>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#8c8c8c' }}>业务做单：</span>
            <div style={{ textAlign: 'right' }}>
              <Tag color={employee.business_order === '是' ? 'green' : 'default'}>{employee.business_order}</Tag>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span style={{ color: '#8c8c8c' }}>备注：</span>
            <div style={{ textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
              <span style={{ whiteSpace: 'pre-wrap' }}>{employee.remark || '无'}</span>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  ), [handleView, showModal, handleDelete]);

  // 表格列配置
  const columns = [
    {
      title: '员工编号',
      dataIndex: 'employee_no',
      key: 'employee_no',
      width: isMobile ? 80 : 100,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: isMobile ? 60 : 80,
    },
    {
      title: '入职日期',
      dataIndex: 'hire_date',
      key: 'hire_date',
      width: isMobile ? 80 : 100,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: '部门岗位',
      dataIndex: 'department_position',
      key: 'department_position',
      width: isMobile ? 80 : 120,
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: isMobile ? 40 : 60,
      render: (gender: string) => (
        <Tag color={gender === '男' ? 'blue' : 'pink'}>
          {gender}
        </Tag>
      ),
    },
    {
      title: '基本工资',
      dataIndex: 'basic_salary',
      key: 'basic_salary',
      width: isMobile ? 70 : 90,
      render: (salary: any) => {
        const numSalary = Number(salary) || 0;
        return `¥${numSalary.toFixed(2)}`;
      },
    },
    {
      title: '业务做单',
      dataIndex: 'business_order',
      key: 'business_order',
      width: isMobile ? 70 : 90,
      render: (order: string) => (
        <Tag color={order === '是' ? 'green' : 'default'}>
          {order}
        </Tag>
      ),
    },
                   {
                 title: '操作',
                 key: 'action',
                 width: isMobile ? 120 : 150,
                 render: (_: any, record: Employee) => (
                   <Space size="small" direction={isMobile ? 'vertical' : 'horizontal'}>
                     <Tooltip title="查看详情">
                       <Button 
                         type="text" 
                         icon={<EyeOutlined />} 
                         size={isMobile ? 'small' : 'middle'}
                         onClick={() => handleView(record)}
                       />
                     </Tooltip>
                                            <Tooltip title="编辑">
                         <Button
                           type="text"
                           icon={<EditOutlined />}
                           size={isMobile ? 'small' : 'middle'}
                           onClick={() => showModal(record)}
                         />
                       </Tooltip>
                     <Popconfirm
                       title="确定要删除这个员工吗？"
                       onConfirm={() => handleDelete(record.id)}
                       okText="确定"
                       cancelText="取消"
                     >
                       <Tooltip title="删除">
                         <Button
                           type="text"
                           danger
                           icon={<DeleteOutlined />}
                           size={isMobile ? 'small' : 'middle'}
                         />
                       </Tooltip>
                     </Popconfirm>
                   </Space>
                 ),
               },
  ];

  return (
    <div style={{ padding: isMobile ? '16px' : '12px' }}>
      {contextHolder}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={24} md={8}>
              <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>员工管理</Title>
            </Col>
            <Col xs={24} sm={24} md={16}>
              <Space 
                direction={isMobile ? 'vertical' : 'horizontal'} 
                style={{ width: '100%', justifyContent: isMobile ? 'stretch' : 'flex-end' }}
                size="small"
              >
                <Input
                  placeholder="搜索员工编号、姓名、部门岗位"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ width: isMobile ? '100%' : 250 }}
                />
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchEmployees}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  刷新
                </Button>
                <Button 
                  icon={<UploadOutlined />} 
                  onClick={() => setImportModalVisible(true)}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  批量导入
                </Button>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'csv',
                        label: '导出CSV',
                        onClick: exportToCSV,
                        icon: <DownloadOutlined />
                      },
                      {
                        key: 'excel',
                        label: '导出Excel',
                        onClick: exportToExcel,
                        icon: <DownloadOutlined />
                      }
                    ]
                  }}
                >
                  <Button 
                    icon={<DownloadOutlined />} 
                    loading={exportLoading}
                    style={{ width: isMobile ? '100%' : 'auto' }}
                  >
                    导出
                  </Button>
                </Dropdown>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={() => showModal()}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  新增员工
                </Button>
              </Space>
            </Col>
          </Row>
          {isMobile && (
            <div style={{ marginTop: 16 }}>
              <span style={{ color: '#8c8c8c' }}>
                共 {total} 个员工（当前页显示 {employees.length} 个）
              </span>
            </div>
          )}
        </div>

        {/* 员工列表 */}
        {isMobile ? (
          // 移动端卡片布局
          <div>
            {employees.map(renderEmployeeCard)}
            {employees.length === 0 && !loading && (
              <Card style={{ textAlign: 'center', padding: '40px' }}>
                <span style={{ color: '#8c8c8c' }}>暂无员工数据</span>
              </Card>
            )}
            
            {/* 移动端分页 */}
            {total > pageSize && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger={false}
                  showQuickJumper={true}
                  showTotal={(total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                  onChange={(page: number, size: number) => {
                    setCurrentPage(page);
                    setPageSize(size || 20);
                  }}
                  size="small"
                />
              </div>
            )}
          </div>
        ) : (
          // 桌面端表格布局
          <Table
            columns={columns}
            dataSource={employees}
            rowKey="id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 20);
              },
            }}
            scroll={{ x: 1200 }}
            size="middle"
          />
        )}
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingEmployee ? '编辑员工' : '新增员工'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="确定"
        cancelText="取消"
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            gender: '男',
            business_order: '否',
            basic_salary: '0',
            position_salary: '0',
            seniority_salary: '0',
          }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="employee_no"
                label="员工编号"
                rules={[{ required: true, message: '请输入员工编号' }]}
              >
                <Input placeholder="请输入员工编号" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="员工姓名"
                rules={[{ required: true, message: '请输入员工姓名' }]}
              >
                <Input placeholder="请输入员工姓名" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="hire_date"
                label="入职日期"
                rules={[{ required: true, message: '请选择入职日期' }]}
              >
                <DatePicker style={{ width: '100%' }} placeholder="请选择入职日期" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="department_position"
                label="部门岗位"
                rules={[{ required: true, message: '请输入部门岗位' }]}
              >
                <Input placeholder="请输入部门岗位" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="basic_salary"
                label="基本工资"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="基本工资"
                  min={0}
                  precision={2}
                  addonAfter="元"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="position_salary"
                label="岗位工资"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="岗位工资"
                  min={0}
                  precision={2}
                  addonAfter="元"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="seniority_salary"
                label="工龄工资"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="工龄工资"
                  min={0}
                  precision={2}
                  addonAfter="元"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="gender"
                label="性别"
                rules={[{ required: true, message: '请选择性别' }]}
              >
                <Select placeholder="请选择性别">
                  {genderOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="birth_date"
                label="出生日期"
              >
                <DatePicker style={{ width: '100%' }} placeholder="请选择出生日期" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="id_card"
                label="身份证号"
              >
                <Input placeholder="请输入身份证号" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="education"
                label="学历"
              >
                <Select placeholder="请选择学历" allowClear>
                  {educationOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="phone"
                label="电话"
              >
                <Input placeholder="请输入电话" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="mobile"
                label="手机"
              >
                <Input placeholder="请输入手机号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="邮箱"
              >
                <Input placeholder="请输入邮箱" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="business_order"
                label="业务做单"
              >
                <Select placeholder="请选择业务做单">
                  {businessOrderOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="住址"
          >
            <Input.TextArea rows={2} placeholder="请输入住址" />
          </Form.Item>

          <Form.Item
            name="remark"
            label="备注"
          >
            <Input.TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
               </Modal>

         {/* 员工详情抽屉 */}
         <Drawer
           title="员工详情"
           placement={isMobile ? 'bottom' : 'right'}
           width={isMobile ? '100%' : 700}
           height={isMobile ? '85%' : undefined}
           open={drawerVisible}
           onClose={() => setDrawerVisible(false)}
         >
           {selectedEmployee && (
             <div>
               <Descriptions title="基本信息" bordered column={1}>
                 <Descriptions.Item label="员工编号">{selectedEmployee.employee_no}</Descriptions.Item>
                 <Descriptions.Item label="姓名">{selectedEmployee.name}</Descriptions.Item>
                 <Descriptions.Item label="入职日期">{selectedEmployee.hire_date ? dayjs(selectedEmployee.hire_date).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
                 <Descriptions.Item label="部门岗位">{selectedEmployee.department_position}</Descriptions.Item>
                 <Descriptions.Item label="性别">
                   <Tag color={selectedEmployee.gender === '男' ? 'blue' : 'pink'}>
                     {selectedEmployee.gender}
                   </Tag>
                 </Descriptions.Item>
                 <Descriptions.Item label="出生日期">{selectedEmployee.birth_date ? dayjs(selectedEmployee.birth_date).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
                 <Descriptions.Item label="身份证号">{selectedEmployee.id_card || '-'}</Descriptions.Item>
                 <Descriptions.Item label="学历">{selectedEmployee.education || '-'}</Descriptions.Item>
                 <Descriptions.Item label="基本工资">¥{(Number(selectedEmployee.basic_salary) || 0).toFixed(2)}</Descriptions.Item>
                 <Descriptions.Item label="岗位工资">¥{(Number(selectedEmployee.position_salary) || 0).toFixed(2)}</Descriptions.Item>
                 <Descriptions.Item label="工龄工资">¥{(Number(selectedEmployee.seniority_salary) || 0).toFixed(2)}</Descriptions.Item>
                 <Descriptions.Item label="电话">{selectedEmployee.phone || '-'}</Descriptions.Item>
                 <Descriptions.Item label="手机">{selectedEmployee.mobile || '-'}</Descriptions.Item>
                 <Descriptions.Item label="邮箱">{selectedEmployee.email || '-'}</Descriptions.Item>
                 <Descriptions.Item label="住址">{selectedEmployee.address || '-'}</Descriptions.Item>
                 <Descriptions.Item label="业务做单">
                   <Tag color={selectedEmployee.business_order === '是' ? 'green' : 'default'}>
                     {selectedEmployee.business_order}
                   </Tag>
                 </Descriptions.Item>
                 <Descriptions.Item label="备注">{selectedEmployee.remark || '-'}</Descriptions.Item>
                 <Descriptions.Item label="创建时间">{selectedEmployee.created_at ? dayjs(selectedEmployee.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
                 <Descriptions.Item label="更新时间">{selectedEmployee.updated_at ? dayjs(selectedEmployee.updated_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
               </Descriptions>
             </div>
           )}
         </Drawer>
       </div>
     );
   };

export default EmployeeManagement;